import {
  CAR_FILTER_KEYS,
  applyCarSearch,
  carSearchParams,
  withSeasonDefault,
} from '../../../util/carSearchParams';
import { carIdOf, normalizeReg } from '../../../util/savedCar';

/* The product a car search filters for when the buyer gave none: complete
 * wheels, the main product (the plate search's product picker asks; a
 * remembered car has no one to ask). */
export const DEFAULT_AD_TYPE = 'wheel';

/**
 * The query string of a car search for this car, written onto `search`: its
 * bolt pattern, offset window and hub bore for the product type in view, plus
 * which car it is — the plate, or for a car chosen by model its car-database
 * id (`carId`, P25009-491). Filters the car search does not own (price,
 * county, sort…) stay. From October to March a search with no season of its
 * own starts on winter (P25009-486 BR-1).
 *
 * @param {Object} options
 * @param {string} options.search  current location.search
 * @param {Object} options.data    the car's payload
 * @param {string} [options.plate] the car's plate; none for a car chosen by model
 * @param {Array}  [options.hubHoleOptions]
 * @param {string} [options.adType] the product to search for; else the URL's
 * @returns {string} the next search string, leading "?" included
 */
export const carSearchQuery = ({ search, data, plate, hubHoleOptions, adType: wantedAdType }) => {
  const current = new URLSearchParams(search || '');
  const adType = wantedAdType || current.get('pub_adType') || DEFAULT_AD_TYPE;
  const params = carSearchParams({ data, adType, hubHoleOptions });
  const next = new URLSearchParams(
    applyCarSearch(
      search,
      current.get('pub_tiretype') ? params : withSeasonDefault(params)
    ).slice(1)
  );
  next.delete('regNumber');
  next.delete('carId');
  const reg = normalizeReg(plate);
  const carId = reg ? null : carIdOf(data);
  if (reg) next.set('regNumber', reg);
  else if (carId) next.set('carId', carId);
  return `?${next.toString()}`;
};

/* The buyer chose these themselves; a remembered car must not override them. */
const OWN_CHOICE_KEYS = ['keywords', 'authorId', 'showByUser'];

/**
 * Should a remembered car turn this search into a car search on its own?
 *
 * Yes when the page is a plain search: no car in the URL yet (no plate, no
 * carId), no fitment or size filters (the product type alone is fine — it
 * says which product to fit), no text search, no single seller. A buyer who
 * picked sizes, typed a keyword or opened a seller's listings keeps exactly
 * that; the car bar's "Bara det som passar" then applies the car's search.
 */
export const shouldApplyRememberedCar = search => {
  const query = new URLSearchParams(search || '');
  if (query.get('regNumber') || query.get('carId')) return false;
  if (CAR_FILTER_KEYS.some(key => key !== 'pub_adType' && query.has(key))) return false;
  return !OWN_CHOICE_KEYS.some(key => query.has(key));
};
